import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  Button,
  Switch,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Save,
  Language,
  Palette,
  Security,
  Person,
  Email,
  CloudSync,
  DataUsage
} from '@mui/icons-material';

import { useColorMode } from '../context/ColorModeContext';
import { useAuth } from '../context/AuthContext';
import NotificationSettings from '../components/notifications/NotificationSettings';

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, setMode } = useColorMode();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'operator';
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [settings, setSettings] = useState({
    language: 'en',
    emailNotifications: true,
    usageAlerts: true,
    dataSync: 'hourly',
    billingTemplate: 'billing1',
    username: 'admin',
    email: 'admin@thynk-h2o.com'
  });

  const handleSettingChange = (setting, value) => {
    setSettings({
      ...settings,
      [setting]: value
    });
  };

  const handleSaveSettings = () => {
    // In a real app, this would save settings to a backend
    console.log('Saving settings:', settings);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 },
      flexGrow: 1,
      maxWidth: '100%',
      mx: 'auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Box sx={{ 
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1,
        mb: 2
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom sx={{ mr: 1 }}>
          Settings
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, flexBasis: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            size={isMobile ? 'small' : 'medium'}
          >
            Save Preferences
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {/* User Profile Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardHeader title="User Profile" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username"
                    size="small"
                    value={settings.username}
                    onChange={(e) => handleSettingChange('username', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    size="small"
                    value={settings.email}
                    onChange={(e) => handleSettingChange('email', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ mt: 1 }}
                    size="medium"
                  >
                    Change Password
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appearance Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardHeader title="Appearance & Language" />
            <Divider />
            <CardContent>
              <List dense disablePadding>
                <ListItem disableGutters secondaryAction={
                  <Switch
                    edge="end"
                    checked={mode === 'dark'}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setMode(next ? 'dark' : 'light');
                    }}
                  />
                }>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Palette color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Dark Mode" />
                </ListItem>
                <ListItem disableGutters secondaryAction={
                  <FormControl sx={{ minWidth: 160 }}>
                    <Select
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      size="small"
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                    </Select>
                  </FormControl>
                }>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Language color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Language" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardHeader title="Notifications" />
            <Divider />
            <CardContent>
              <List dense disablePadding>
                <ListItem disableGutters secondaryAction={
                  <Switch
                    edge="end"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  />
                }>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Email color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email Notifications" 
                    secondary="Receive reports and alerts via email"
                  />
                </ListItem>
                <ListItem disableGutters secondaryAction={
                  <Switch
                    edge="end"
                    checked={settings.usageAlerts}
                    onChange={(e) => handleSettingChange('usageAlerts', e.target.checked)}
                  />
                }>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <DataUsage color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Usage Alerts" 
                    secondary="Get notified when usage exceeds thresholds"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* System Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardHeader title="System Settings" />
            <Divider />
            <CardContent>
              <List dense disablePadding>
                <ListItem disableGutters secondaryAction={
                  <FormControl sx={{ minWidth: 160 }}>
                    <Select
                      value={settings.dataSync}
                      onChange={(e) => handleSettingChange('dataSync', e.target.value)}
                      size="small"
                    >
                      <MenuItem value="realtime">Real-time</MenuItem>
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="manual">Manual</MenuItem>
                    </Select>
                  </FormControl>
                }>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CloudSync color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Data Synchronization" />
                </ListItem>
                <ListItem disableGutters secondaryAction={
                  <FormControl sx={{ minWidth: 160 }}>
                    <Select
                      value={settings.billingTemplate}
                      onChange={(e) => handleSettingChange('billingTemplate', e.target.value)}
                      size="small"
                    >
                      <MenuItem value="billing1">Standard Water</MenuItem>
                      <MenuItem value="billing2">Detailed Water</MenuItem>
                      <MenuItem value="billing3">Standard Electricity</MenuItem>
                    </Select>
                  </FormControl>
                }>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Default Billing Template" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {isAdmin && (
          <Grid item xs={12}>
            <NotificationSettings onSettingsUpdated={() => {}} />
          </Grid>
        )}

      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
